import * as React from 'react';
import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import CancelButton from './buttons/CancelButton';
import { useNavigate } from 'react-router-dom';
import { 
  bindingAccount, 
  updateTransactionStatus, 
  getTransactionResult,
  getPaymentCredentials
} from '../libs/GsmpiApi';
import { 
  TRANSACTION_STATUS,
  POLLING_TIME 
} from '../common/constants';
import { setCookieDetails } from '../data/cookieDetailsSlice';
import '../App.css';
import styles from '../css/pc/PcGeneral.module.css';
import '../css/pc/AccountBindingByCookieTab.css';
import classNames from 'classnames/bind';
import JSEncrypt from 'jsencrypt';
import { RSA, POST_MESSAGE_CONTENT_MESSAGE } from '../common/constants';
import { getCookieDetails } from '../utils/cookieDetails';
import debug from 'debug';

const cx = classNames.bind(styles);
const log = debug('app:AccountBindingByCookieTab');

export default function AccountBindingByCookieTab() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [statusPollingCountDown, setStatusPollingCountDown] = useState(POLLING_TIME.EMAIL_POLL);
  const { serviceId, transactionId, domains } = useSelector((state) => state.paymentClient);
  const { cookieValue, hasCookies }  = useSelector((state) => state.cookieDetails);
  const browserId = useSelector((state) => state.browserDetails.browserId);
  const dispatch = useDispatch();
  const abortControllersRef = useRef([]);

  useEffect(() => {
    const abortController = new AbortController();
    abortControllersRef.current.push(abortController);
    const abortSignal = abortController.signal;

    // Countdown that continuously polls the status of the transaction then does redirection based on the polled status
    if (statusPollingCountDown > 0) {
      const pollingInterval = setInterval(() => {
        setStatusPollingCountDown(prevStatusPollingCountDown => prevStatusPollingCountDown > 0 ? prevStatusPollingCountDown - 1.5 : 0);

        getTransactionResult(transactionId, false, abortSignal)
          .then(result => {
            if (result.data.status === TRANSACTION_STATUS.CONFIRMED) {
              setStatusPollingCountDown(0);
            }

            handlePollingResult(result);
          })
          .catch(error => {
            handlePollingError(error);
          });
      }, 1500);

      return () => clearInterval(pollingInterval);
    }
  }, [statusPollingCountDown]);

  const handlePollingResult = (result) => {
    if (result.data.status === TRANSACTION_STATUS.CONFIRMED) {
      cancelPollingRequests();
      handleConfirmedTransaction(result.data.refId, getCookieDetails(result.headers.get("x-smps-gsmpi-cookie-id")));
      setStatusPollingCountDown(0);
    } else if (result.data.status === TRANSACTION_STATUS.EXPIRED) {
      navigate('/gsmpi-web/timeout', {
        state: {
          timedOutType: POST_MESSAGE_CONTENT_MESSAGE.PAYMENT_TIMEOUT
        }
      });
    } else if (result.data.status === TRANSACTION_STATUS.CANCELED) {
      navigate('/gsmpi-web/cancel');
    } else if (result.data.status === TRANSACTION_STATUS.ERRED) {
      navigate('/gsmpi-web/error');
    } else {
      log(`result status : ${result.data.status}`);
    }
  };

  const handleConfirmedTransaction = (refId, cookieDetails) => {
    getPaymentCredentials({refId: refId, serviceId: serviceId})
      .then((result) => {
        const state = {
          state: {
            paymentCredential: result.data,
            cookie: cookieDetails,
            referenceId: refId
          }
        };

        navigate('/gsmpi-web/done', state);
      })
      .catch(() => navigate('/gsmpi-web/error'));
  };

  const handlePollingError = (error) => {
    if (error.response.data.resultCode === 'OPM2N1101' || error.response.data.resultCode === 'OPM1N1039') {
      //transaction is expired
      navigate('/gsmpi-web/timeout', {
        state: {
          timedOutType: POST_MESSAGE_CONTENT_MESSAGE.PAYMENT_TIMEOUT
        }
      });
    } else if (error.response.data.resultCode === 'OPM2N1103' || error.response.data.resultCode === 'OPM2N1104') {
      //transaction is invalid
      navigate('/gsmpi-web/error');
    } else {
      navigate('/gsmpi-web/error');
    }
  };

  const cancelPollingRequests = () => {
    abortControllersRef.current.forEach(controller => controller.abort());
    abortControllersRef.current = [];
  };

  async function bindAccountRequest({request, params}) {

    await bindingAccount(params.transactionId, request)
      .catch((error) => {
        throw error;
      });

    log('done bindingAccount api call');

    return {
      transactionId: params.transactionId,
      email: request.saEmail,
      useCookie: true,
      useAccount: false
    };
  }

  const handleSubmit = (e) => {
    e.preventDefault();

    const jsEncrypt = new JSEncrypt();
    jsEncrypt.setPublicKey(RSA.PUBLIC_KEY);

    const requestWithParams = {
      request: {
        serviceId: serviceId,
        saEmail: jsEncrypt.encrypt(cookieValue),
        browserId: browserId,
        domains: domains,
        useCookie: hasCookies,
        useAccount: false
      },
      params: {
        transactionId: transactionId
      }
    };
    
    bindAccountRequest(requestWithParams)
      .then(result => navigate('/gsmpi-web/time-remaining', {state: result}))
      .catch(error => {
        if (error.resultCode === 'OPM2N1101' || error.resultCode === 'OPM1N1039') {  //transaction is expired
          navigate('/gsmpi-web/timeout');
        } else if (error.resultCode === 'OPM2N1103' || error.resultCode === 'OPM2N1104') {  //transaction is invalid
          navigate('/gsmpi-web/error');
        } else if (error.resultCode === 'OPM1N1041') {  //retry count exceeded
          const cancelPayload = {
            payment: {
              status: TRANSACTION_STATUS.EXPIRED,
              reason: 'Exceeded retry count'
            },
            timestamp: new Date().getTime()
          };

          updateTransactionStatus(transactionId, cancelPayload)
            .then(() => {
              navigate('/gsmpi-web/cancel');
            });
        } else {
          navigate('/gsmpi-web/error');
        }
      });
  };

  const handleUseAnotherAccount = () => {
    const newCookieDetails = {
      cookieValue: undefined,
      hasCookies: false
    };

    dispatch(setCookieDetails(newCookieDetails));
  };

  return (
    <>
      <div className={cx('mpi-content')}>
        <div className="mpi-cookie-requestpay">
          <p className={cx('mpi-requestpay-text')}>{t('pay.gsmpi.withCookie.noti1')}</p>
          <div className="mpi-requestpay-email">{cookieValue}</div>
          <a href="javascript:void(0);" className="mpi-requestpay-another" onClick={handleUseAnotherAccount}>
            {t('pay.gsmpi.withCookie.msg2')}
          </a>
          {/* app not supported error prompt */}
          { cookieValue && hasCookies === false ? <p className="asmpi-errmsg">{t('pay.gsmpi.withCookie.errmsg1')}</p> : null }
        </div>
      </div>
      <div className={cx('mpi-boxbottom')}>
        <div className={`${cx('mpi-button')} ${cx('is-clearfix')}`}>
          <CancelButton className={cx('mpi-button-cancel')} transactionId={transactionId} />
          <button type="button" className={cx('mpi-button-submit')} onClick={handleSubmit}>
            {t('pay.gsmpi.withCookie.button2')}
          </button>
        </div>
      </div>
    </>
  );
}